/**
 * Motor 3D mínimo en SVG, sin librerías: mallas de caras planas, giro sobre
 * el eje vertical, proyección ortográfica, sombreado por luz fija y orden de
 * pintado (de lo más lejano a lo más cercano). Suficiente para objetos
 * sencillos como cajas, paneles y cables, y pesa unos pocos KB.
 */

export type V3 = [number, number, number];

export interface Cara {
  pts: V3[];
  color: string;
  /** Normal conocida (cajas); si falta se calcula y se orienta hacia afuera. */
  n?: V3;
  /** Calcomanía pegada sobre otra cara (pantallas, ventanas…): se pinta encima. */
  calco?: boolean;
  /** Sin sombreado (luces, pantallas encendidas). */
  plano?: boolean;
  /** Contorno oscuro de ilustración (sí por defecto; no en losetas). */
  borde?: boolean;
}

export interface Malla {
  caras: Cara[];
  /** Orden de pintado por capas: 0 = terreno, 0.5 = piso, 1 = objetos */
  capa?: number;
}

export interface Linea {
  pts: V3[];
  color: string;
  ancho: number;
  /** Clave del cable para animar el flujo de energía */
  id?: string;
  /** Capa de pintado (1 por defecto) */
  capa?: number;
  /** [z, capa]: los tramos a esa altura o más arriba van en esa capa (p. ej. sobre la losa) */
  capaAlta?: [number, number];
  /** Dibujar una flecha en la punta cuando lleva energía */
  flecha?: boolean;
}

/** Figura plana (planta, arbusto…) que siempre mira a la cámara. */
export interface Figura {
  pos: V3;
  tipo: 'maceta' | 'arbusto';
  escala?: number;
  capa?: number;
}

export interface Vista {
  /** Giro alrededor del eje vertical, en radianes */
  theta: number;
  /** Inclinación de la cámara (0 = de frente, π/2 = desde arriba) */
  phi: number;
  escala: number;
}

export type Dibujable =
  | { tipo: 'cara'; d: string; fill: string; prof: number; capa: number; borde: boolean }
  | { tipo: 'figura'; x: number; y: number; figura: Figura['tipo']; escala: number; prof: number; capa: number }
  | { tipo: 'linea'; d: string; color: string; ancho: number; prof: number; capa: number; id?: string };

// ─── Vectores ────────────────────────────────────────────────────────────────

const resta = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const punto = (a: V3, b: V3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cruz = (a: V3, b: V3): V3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
const normaliza = (a: V3): V3 => {
  const l = Math.hypot(a[0], a[1], a[2]) || 1;
  return [a[0] / l, a[1] / l, a[2] / l];
};
const centro = (pts: V3[]): V3 => {
  const s: V3 = [0, 0, 0];
  for (const p of pts) { s[0] += p[0]; s[1] += p[1]; s[2] += p[2]; }
  return [s[0] / pts.length, s[1] / pts.length, s[2] / pts.length];
};

/** Largo máximo de cada tramo de cable al ordenarlo (unidades de la escena). */
const TRAMO = 4;

/** Luz del sol fija en la escena (arriba, a la izquierda y al frente). */
const LUZ = normaliza([-0.45, -0.75, 1.1]);

function sombrea(hex: string, n: V3): string {
  // Sombreado suave, de ilustración: dos o tres tonos por objeto.
  const f = 0.8 + 0.2 * Math.max(0, punto(n, LUZ));
  const v = parseInt(hex.slice(1), 16);
  const c = (s: number) => Math.min(255, Math.round(((v >> s) & 255) * f));
  return `rgb(${c(16)},${c(8)},${c(0)})`;
}

// ─── Proyección ──────────────────────────────────────────────────────────────

export function proyector(v: Vista) {
  const cos = Math.cos(v.theta), sin = Math.sin(v.theta);
  const cp = Math.cos(v.phi), sp = Math.sin(v.phi);
  const gira = (p: V3): V3 => [p[0] * cos - p[1] * sin, p[0] * sin + p[1] * cos, p[2]];
  /** Dirección en que mira la cámara (hacia el fondo y hacia abajo). */
  const mira: V3 = [0, cp, -sp];
  const pantalla = (p: V3): [number, number] => {
    const r = gira(p);
    return [r[0] * v.escala, -(r[1] * sp + r[2] * cp) * v.escala];
  };
  const profundidad = (p: V3) => punto(gira(p), mira);
  return { gira, mira, pantalla, profundidad };
}

const ruta = (xy: [number, number][], cerrada: boolean) =>
  xy.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join('') + (cerrada ? 'Z' : '');

/** Todo lo visible, ya ordenado para pintarlo en ese orden. */
export function renderiza(mallas: Malla[], lineas: Linea[], vista: Vista, figuras: Figura[] = []): Dibujable[] {
  const P = proyector(vista);
  const out: Dibujable[] = [];

  for (const m of mallas) {
    const cm = centro(m.caras.flatMap((c) => c.pts));
    // Profundidad de cada cara "base" según su normal: las calcomanías usan la
    // de la cara donde van pegadas, para pintarse siempre justo encima de ella.
    const base = new Map<string, number>();
    for (const c of m.caras) {
      if (!c.calco && c.n) base.set(c.n.join(), P.profundidad(centro(c.pts)));
    }
    let orden = 0;
    for (const c of m.caras) {
      const cc = centro(c.pts);
      let n = c.n;
      if (!n) {
        n = normaliza(cruz(resta(c.pts[1], c.pts[0]), resta(c.pts[2], c.pts[0])));
        if (punto(n, resta(cc, cm)) < 0) n = [-n[0], -n[1], -n[2]];
      }
      // Cara de espaldas a la cámara: no se ve.
      if (punto(P.gira(n), P.mira) >= 0) continue;
      out.push({
        tipo: 'cara',
        d: ruta(c.pts.map(P.pantalla), true),
        fill: c.plano ? c.color : sombrea(c.color, n),
        prof: c.calco && c.n && base.has(c.n.join())
          ? base.get(c.n.join())! - 0.001 * ++orden
          : P.profundidad(cc) - (c.calco ? 0.3 : 0),
        capa: m.capa ?? 1,
        borde: c.borde ?? true,
      });
    }
  }

  // Los cables se pintan por tramos cortos: así uno que pasa por detrás de algo
  // (por ejemplo, bajando a través de la losa) queda tapado solo en esa parte.
  for (const l of lineas) {
    for (let i = 0; i < l.pts.length - 1; i++) {
      const a = l.pts[i], b = l.pts[i + 1];
      const n = Math.max(1, Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]) / TRAMO));
      for (let j = 0; j < n; j++) {
        const p = (t: number): V3 => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
        const p0 = p(j / n), p1 = p((j + 1) / n);
        const zm = (p0[2] + p1[2]) / 2;
        const capa = l.capaAlta && zm >= l.capaAlta[0] ? l.capaAlta[1] : l.capa ?? 1;
        out.push({
          tipo: 'linea',
          d: ruta([P.pantalla(p0), P.pantalla(p1)], false),
          color: l.color,
          ancho: l.ancho,
          prof: P.profundidad(centro([p0, p1])) - 0.4,
          capa,
          id: l.id,
        });
      }
    }
  }

  for (const f of figuras) {
    const [x, y] = P.pantalla(f.pos);
    out.push({ tipo: 'figura', x, y, figura: f.tipo, escala: f.escala ?? 1, prof: P.profundidad(f.pos) - 0.5, capa: f.capa ?? 1 });
  }

  return out.sort((a, b) => a.capa - b.capa || b.prof - a.prof);
}

// ─── Constructores ───────────────────────────────────────────────────────────

/** Caja alineada a los ejes: esquina (x, y, z) y medidas (ancho, fondo, alto). */
export function caja(
  x: number, y: number, z: number, w: number, d: number, h: number,
  color: string | { arriba?: string; lados: string; frente?: string },
  capa = 1,
): Malla {
  const col = typeof color === 'string' ? { lados: color } : color;
  const X = x + w, Y = y + d, Z = z + h;
  const lados = col.lados;
  return {
    capa,
    caras: [
      { pts: [[x, y, Z], [X, y, Z], [X, Y, Z], [x, Y, Z]], n: [0, 0, 1], color: col.arriba ?? lados },
      { pts: [[x, y, z], [X, y, z], [X, Y, z], [x, Y, z]], n: [0, 0, -1], color: lados },
      { pts: [[x, y, z], [X, y, z], [X, y, Z], [x, y, Z]], n: [0, -1, 0], color: col.frente ?? lados },
      { pts: [[x, Y, z], [X, Y, z], [X, Y, Z], [x, Y, Z]], n: [0, 1, 0], color: lados },
      { pts: [[x, y, z], [x, Y, z], [x, Y, Z], [x, y, Z]], n: [-1, 0, 0], color: lados },
      { pts: [[X, y, z], [X, Y, z], [X, Y, Z], [X, y, Z]], n: [1, 0, 0], color: lados },
    ],
  };
}

/** Rectángulo pegado sobre la cara frontal (y constante, mira hacia -y). */
export function calcoFrente(x: number, y: number, z: number, w: number, h: number, color: string, plano = false): Cara {
  const yy = y - 0.02;
  return { pts: [[x, yy, z], [x + w, yy, z], [x + w, yy, z + h], [x, yy, z + h]], n: [0, -1, 0], color, calco: true, plano };
}

/** Rectángulo pegado sobre una cara lateral derecha (x constante, mira hacia +x). */
export function calcoDerecha(x: number, y: number, z: number, d: number, h: number, color: string, plano = false): Cara {
  const xx = x + 0.02;
  return { pts: [[xx, y, z], [xx, y + d, z], [xx, y + d, z + h], [xx, y, z + h]], n: [1, 0, 0], color, calco: true, plano };
}

/** Círculo (polígono de 14 lados) pegado sobre la cara frontal. */
export function discoFrente(cx: number, y: number, cz: number, r: number, color: string, plano = false): Cara {
  const yy = y - 0.03;
  const pts: V3[] = [];
  for (let i = 0; i < 14; i++) {
    const a = (i / 14) * Math.PI * 2;
    pts.push([cx + Math.cos(a) * r, yy, cz + Math.sin(a) * r]);
  }
  return { pts, n: [0, -1, 0], color, calco: true, plano };
}

/**
 * Superficie plana en losetas pequeñas (losas, paredes). Una sola cara muy
 * grande se ordenaría mal frente a lo que tiene delante o encima.
 */
export function losetas(
  eje: 'z' | 'y' | 'x', fijo: number, a0: number, a1: number, b0: number, b1: number,
  paso: number, color: string, n: V3,
): Cara[] {
  const caras: Cara[] = [];
  for (let a = a0; a < a1 - 1e-6; a += paso) {
    for (let b = b0; b < b1 - 1e-6; b += paso) {
      const A = Math.min(a1, a + paso), B = Math.min(b1, b + paso);
      const q: [number, number][] = [[a, b], [A, b], [A, B], [a, B]];
      const pts: V3[] = q.map(([u, v]) =>
        eje === 'z' ? [u, v, fijo] : eje === 'y' ? [u, fijo, v] : [fijo, u, v]);
      caras.push({ pts, n, color, borde: false });
    }
  }
  return caras;
}

/**
 * Pieza con forma de perfil (en el plano x–z) extruido a lo ancho (y0 → y1):
 * sirve para siluetas como la de un coche. El perfil debe ser convexo.
 * `bordes[i]` da el color de la cara que une el punto i con el i+1 (techo,
 * parabrisas…); si falta se usa `color`.
 */
export function extruye(
  perfil: [number, number][], y0: number, y1: number, color: string,
  bordes: (string | undefined)[] = [], capa = 1,
): Malla {
  const a: V3[] = perfil.map(([x, z]) => [x, y0, z]);
  const b: V3[] = perfil.map(([x, z]) => [x, y1, z]);
  const caras: Cara[] = [
    { pts: a, n: [0, -1, 0], color },
    { pts: [...b].reverse(), n: [0, 1, 0], color },
  ];
  for (let i = 0; i < perfil.length; i++) {
    const j = (i + 1) % perfil.length;
    caras.push({ pts: [a[i], a[j], b[j], b[i]], color: bordes[i] ?? color });
  }
  return { caras, capa };
}

/** Polígono pegado sobre una cara que mira hacia -y (calcomanía de forma libre). */
export function calcoPerfil(pts: [number, number][], y: number, color: string, plano = false): Cara {
  return { pts: pts.map(([x, z]) => [x, y - 0.03, z] as V3), n: [0, -1, 0], color, calco: true, plano };
}
